import type { GettingStartedStatus } from '@/lib/services/onboarding/types';

/**
 * The one predicate every Getting Started surface binds to.
 *
 * **There are three surfaces and there used to be three answers.** The sign-in
 * prompt had a real eligibility rule — grandfathering at the feature's launch
 * date, `billing_only` excluded — and the hub, the sidebar widget and the icon
 * rail had none at all: they rendered until `dismissed` was true and checked
 * nothing else, which showed a six-step setup checklist to 78 of 112 production
 * users who were either grandfathered or already finished. Transcribing the
 * backend's list into three components is what drifted in the first place and is
 * what VALE-1265 already had to fix once in the step destinations, so the rule is
 * SERVED (`status.applicable`) and combined here, once.
 *
 * Two distinct questions, and conflating them is the bug this file exists to
 * prevent:
 *
 * - **`canReachGettingStarted`** — may this user get to the checklist at all?
 *   Governs the sidebar entry and the icon rail.
 * - **`shouldLandOnGettingStarted`** — should we PUT them there without being
 *   asked? Governs the two unprompted landings.
 *
 * `dismissed` answers only the second. That is the whole of what Dismiss means
 * now: "don't open this on its own." It used to hide the sidebar entry and the
 * rail too, which left a dismissed user with no route back anywhere in the app —
 * while two pieces of copy told them there was one.
 */

/**
 * Whether this user may reach the checklist, via the sidebar entry and the icon
 * rail.
 *
 * Gated on `reachable` and nothing else, and each omission is deliberate:
 *
 * - **Not `dismissed`** — those two surfaces are the permanent, low-footprint
 *   way back in, and removing them is what made Dismiss irreversible.
 * - **Not `applicable`** — that carries the grandfathering conjunct, which is a
 *   *trigger* rule about not ambushing a long-standing user, not a statement
 *   that the checklist is useless to them. Gating the entry on it would hide it
 *   from 74 of 112 production users and make the setup flow's own promise that
 *   they can pick this up "whenever you like from Getting started" false.
 * - **Not completion** — a finished user's entry shows 6/6, and it is also how
 *   they get back to the "Go further" material.
 */
export function canReachGettingStarted(
  status: GettingStartedStatus | null | undefined
): boolean {
  if (!status) return false;
  return status.reachable;
}

/**
 * Whether to land this user on the hub without their asking — the `/onboarding`
 * route's bounce when nothing is being prompted, and the first-sign-in flow's
 * exit.
 *
 * Three ways to say no, and each is a real production population: they asked not
 * to be (`dismissed`, 16 users), the checklist does not apply to them
 * (`applicable`, 74 grandfathered plus every billing-only contact), or they have
 * finished it.
 */
export function shouldLandOnGettingStarted(
  status: GettingStartedStatus | null | undefined
): boolean {
  if (!status) return false;
  if (status.dismissed) return false;
  if (!status.applicable) return false;
  return status.completedCount < status.totalCount;
}

/** Whether every step is done. */
export function isGettingStartedComplete(
  status: GettingStartedStatus | null | undefined
): boolean {
  if (!status) return false;
  return status.completedCount >= status.totalCount;
}
